// F18-R5 — LiveOps event preview page.
// Shows a mobile UI mockup of how the event will appear to users.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi, getErrorMessage, type LiveopsEvent } from '../lib/api';

export default function EventPreview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<LiveopsEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const data = await adminApi.listLiveops();
        const found = data.events.find((e) => e.id === decodeURIComponent(id));
        if (!found) {
          setError('Event not found');
        } else {
          setEvent(found);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="p-4 text-gray-400">Loading event...</div>;
  if (error) return <div className="p-4 text-red-400">{error}</div>;
  if (!event) return <div className="p-4 text-gray-400">Event not found</div>;

  const now = Date.now();
  const isActive = event.is_active && event.start_ms <= now && event.end_ms > now;
  const remainingMs = event.end_ms - now;
  const remainingDays = Math.max(0, Math.floor(remainingMs / (24 * 60 * 60 * 1000)));
  const remainingHours = Math.max(0, Math.floor((remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/liveops')}
          className="text-gray-400 hover:text-white"
        >&larr; Back to Live Ops</button>
        <h2 className="text-xl font-bold text-white">Event Preview: {event.id}</h2>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Mobile UI Mockup */}
        <div className="rounded-lg border border-gray-700 bg-gray-900 p-6">
          <h3 className="mb-4 text-sm font-medium text-gray-400">Mobile App Appearance</h3>

          {/* Phone frame */}
          <div className="mx-auto max-w-sm rounded-[2rem] border-4 border-gray-800 bg-black p-4 shadow-xl">
            {/* Banner */}
            <div className="rounded-xl bg-gradient-to-r from-orange-600 to-red-600 p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Live Ops Event</p>
                  <p className="text-lg font-bold">
                    {event.emoji ? `${event.emoji} ` : ''}{event.title}
                  </p>
                </div>
                <div className="text-right">
                  {event.target_type === 'multiplier' ? (
                    <>
                      {event.xp_multiplier > 1 && (
                        <p className="text-2xl font-extrabold text-yellow-300">{event.xp_multiplier}x XP</p>
                      )}
                      {event.gs_multiplier > 1 && (
                        <p className="text-2xl font-extrabold text-purple-300">{event.gs_multiplier}x GS</p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-extrabold">+{event.bonus_gs}</p>
                      <p className="text-xs opacity-80">Bonus GS</p>
                    </>
                  )}
                </div>
              </div>
              {isActive && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-green-400"></span>
                  <span>{remainingDays}d {remainingHours}h remaining</span>
                </div>
              )}
              {!isActive && event.start_ms > now && (
                <div className="mt-2 text-xs opacity-80">Starts soon</div>
              )}
              {!isActive && event.end_ms <= now && (
                <div className="mt-2 text-xs opacity-60">Event ended</div>
              )}
            </div>

            {/* Event detail card */}
            <div className="mt-4 rounded-xl bg-gray-900 p-4">
              <p className="text-sm text-gray-300">{event.description ?? 'No description'}</p>

              {event.premium_only && (
                <div className="mt-3 inline-block rounded bg-yellow-900/50 px-2 py-1 text-xs text-yellow-400">
                  Premium Only
                </div>
              )}

              {event.premium_bonus_gs > 0 && (
                <div className="mt-3 rounded bg-purple-900/30 p-2 text-xs text-purple-300">
                  Premium members get +{event.premium_bonus_gs} extra GS
                </div>
              )}

              {/* Targets */}
              {event.target_type !== 'multiplier' && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-400">Targets ({event.target_type})</p>
                  {event.target_type === 'venue_set' && (
                    <div className="mt-1 space-y-1">
                      {event.venue_ids.slice(0, 5).map((vid) => (
                        <div key={vid} className="text-xs text-gray-300">
                          📍 {vid}
                        </div>
                      ))}
                      {event.venue_ids.length > 5 && (
                        <div className="text-xs text-gray-500">+{event.venue_ids.length - 5} more</div>
                      )}
                    </div>
                  )}
                  {event.target_type !== 'venue_set' && event.dish_targets && (
                    <div className="mt-1 space-y-1">
                      {event.dish_targets.map((dt, i) => (
                        <div key={i} className="text-xs text-gray-300">
                          🍽️ {dt.dish_name} ({dt.required_count}x)
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {event.target_type === 'multiplier' && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-400">Multiplier Effect</p>
                  <div className="mt-1 space-y-1">
                    {event.xp_multiplier > 1 && (
                      <div className="text-xs text-yellow-300">⚡ {event.xp_multiplier}x XP for all check-ins</div>
                    )}
                    {event.gs_multiplier > 1 && (
                      <div className="text-xs text-purple-300">⭐ {event.gs_multiplier}x GS for all check-ins</div>
                    )}
                    {event.region_filter && (
                      <div className="text-xs text-gray-400">📍 Region: {event.region_filter}</div>
                    )}
                    <div className="text-xs text-gray-400">👥 {event.participant_count} participants</div>
                  </div>
                </div>
              )}

              {/* Join button */}
              <button className="mt-4 w-full rounded-lg bg-orange-600 py-2 text-sm font-bold text-white">
                Join Event
              </button>
            </div>
          </div>
        </div>

        {/* Event metadata */}
        <div className="space-y-4">
          <div className="rounded border border-gray-700 bg-gray-850 p-4">
            <h3 className="mb-3 text-sm font-medium text-gray-400">Event Details</h3>
            <dl className="space-y-2 text-sm">
              <div><dt className="inline text-gray-500">ID:</dt> <dd className="inline font-mono text-gray-300">{event.id}</dd></div>
              <div><dt className="inline text-gray-500">Title:</dt> <dd className="inline text-white">{event.title}</dd></div>
              <div><dt className="inline text-gray-500">Type:</dt> <dd className="inline text-gray-300">{event.target_type}</dd></div>
              {event.target_type === 'multiplier' ? (
                <>
                  {event.xp_multiplier > 1 && <div><dt className="inline text-gray-500">XP Mult:</dt> <dd className="inline text-yellow-400">{event.xp_multiplier}x</dd></div>}
                  {event.gs_multiplier > 1 && <div><dt className="inline text-gray-500">GS Mult:</dt> <dd className="inline text-purple-400">{event.gs_multiplier}x</dd></div>}
                  {event.region_filter && <div><dt className="inline text-gray-500">Region:</dt> <dd className="inline text-gray-300">{event.region_filter}</dd></div>}
                  {event.emoji && <div><dt className="inline text-gray-500">Emoji:</dt> <dd className="inline text-gray-300">{event.emoji}</dd></div>}
                  <div><dt className="inline text-gray-500">Participants:</dt> <dd className="inline text-gray-300">{event.participant_count}</dd></div>
                </>
              ) : (
                <>
                  <div><dt className="inline text-gray-500">Bonus GS:</dt> <dd className="inline text-orange-400">+{event.bonus_gs}</dd></div>
                  <div><dt className="inline text-gray-500">Premium Bonus:</dt> <dd className="inline text-purple-400">+{event.premium_bonus_gs}</dd></div>
                </>
              )}
              <div><dt className="inline text-gray-500">Premium Only:</dt> <dd className="inline text-gray-300">{event.premium_only ? 'Yes' : 'No'}</dd></div>
              <div><dt className="inline text-gray-500">Active:</dt> <dd className="inline text-gray-300">{event.is_active ? 'Yes' : 'No'}</dd></div>
              <div><dt className="inline text-gray-500">Start:</dt> <dd className="inline text-gray-300">{new Date(event.start_ms).toLocaleString()}</dd></div>
              <div><dt className="inline text-gray-500">End:</dt> <dd className="inline text-gray-300">{new Date(event.end_ms).toLocaleString()}</dd></div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
